import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { resetPassword } from "../services/api";
import { useAppConfig } from "../context/AppConfigContext";
import { useTranslation } from "../i18n/LanguageContext";
import LanguageSwitcher from "../components/LanguageSwitcher";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { appName, branding, loginHeader, loginLogo } = useAppConfig();
  const { t, i18nEnabled } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError(t("login.resetPasswordMismatch"));
      return;
    }

    if (!token) {
      setError(t("login.resetInvalidToken"));
      return;
    }

    setLoading(true);
    try {
      await resetPassword({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || t("login.resetInvalidToken"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        {i18nEnabled && (
          <div className="flex justify-end mb-2">
            <LanguageSwitcher />
          </div>
        )}
        {loginHeader === "logo" ? (
          <div className="flex justify-center mb-2">
            <img
              src={loginLogo || branding.logo}
              alt={appName}
              className="h-36 object-contain"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        ) : (
          <h1 className="text-3xl font-bold text-center text-gray-900 mb-2">
            {appName}
          </h1>
        )}
        <p className="text-center text-gray-500 mb-2">{t("login.resetTitle")}</p>
        <p className="text-center text-gray-400 text-sm mb-6">{t("login.resetDesc")}</p>

        {success ? (
          <div className="space-y-4">
            <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-green-700 text-sm">
              {t("login.resetSuccess")}
            </div>
            <Link
              to="/login"
              className="block w-full py-2.5 text-center bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t("login.submit")}
            </Link>
          </div>
        ) : (
          <>
            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  {t("login.newPassword")}
                </label>
                <input
                  id="password"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  {t("login.confirmPassword")}
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={8}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? t("login.resetSubmitting") : t("login.resetSubmit")}
              </button>
            </form>

            <p className="mt-6 text-center text-sm text-gray-500">
              <Link to="/login" className="text-blue-600 font-medium hover:text-blue-500">
                {t("login.forgotBack")}
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
